import styled from '@emotion/styled';
import Global from '@emotion/styled';
import { pxToRem, theme, media } from '@vfit/shared/themes';
import { IShowDetails } from './stickyOffer.model';
import { css, keyframes } from '@emotion/react';

export const progressbarHeight = 6;

const OpaqueActive = css`
  width: 100vw;
  height: 100vh;
  animation: fade-in 0.9s ease-in forwards;
  background-color: ${theme.colors.bg.overlay};

  // z-index: 20;

  @keyframes fade-in {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }
`;

export const OpaqueBackground = styled.div<{ isActive: IShowDetails }>`
  bottom: 0;
  left: 0;
  opacity: 0;
  position: fixed;
  ${(p) => (p.isActive == 'slide-up' ? OpaqueActive : '')}
`;

export const MainContainer = styled.div<{ activationBoxHeight: number; isApp?: boolean }>`
  z-index: 4;
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  height: ${(p) => (p.isApp ? '80vh' : '100%')};
  transform: translate3d(0, calc(100% - ${(props) => `${props.activationBoxHeight}px`}), 0);
  background-color: ${theme.colors.bg.primary};
  border-radius: ${pxToRem(6, 6, 0, 0)};
  overflow: hidden;

  &.slide-up {
    @keyframes slide-out-up {
      from {
        transform: translate3d(0, calc(100% - ${(props) => `${props.activationBoxHeight}px`}), 0);
      }

      to {
        transform: translate3d(0, 0, 0);
      }
    }

    animation-name: slide-out-up;
    animation-duration: 0.8s;
    animation-fill-mode: forwards;
  }

  &.slide-down {
    @keyframes slide-out-down {
      from {
        transform: translate3d(0, 0, 0);
      }

      to {
        transform: translate3d(0, calc(100% - ${(props) => `${props.activationBoxHeight}px`}), 0);
      }
    }

    animation-name: slide-out-down;
    animation-duration: 0.8s;
    animation-fill-mode: forwards;
  }

  &.upward {
    animation: increase-up 0.4s forwards ease-out;
  }

  &.downward {
    animation: decrease 0.4s forwards ease-out;
  }

  @keyframes increase-up {
    from {
      bottom: -140px;
    }

    to {
      bottom: 0;
    }
  }

  @keyframes decrease {
    from {
      bottom: 0;
    }

    to {
      bottom: -140px;
    }
  }
`;

export const OverlayGlobalStyle = Global.style`
  body {
  overflow: hidden;
  }
`;

export const ActivationBox = styled.div<{ withProgressbar: boolean | undefined }>`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.md};

  ${media.tablet`
      padding: ${theme.spacing.md} ${theme.spacing.xl};
  `}

  ${media.desktop`
      padding: 20px 80px;
  `}

  border-top: ${(props) =>
    props.withProgressbar ? `unset` : `0.5px solid ${theme.colors.border.default}`};
  border-bottom: 0.5px solid ${theme.colors.border.default};
`;

export const CtaContainer = styled.div`
  min-width: 123px;
  max-width: 65%;

  button,
  a {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: unset;
    min-width: ${pxToRem(97)};

    ${media.tablet`
      min-width: ${pxToRem(209)};
  `}

    ${media.desktop`
      min-width: ${pxToRem(220)};
  `}
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1); /* Initial size */
  }
  50% {
    transform: scale(1.3); /* Bigger */
  }
  75% {
    transform: scale(1.3); /* Hold at bigger size */
  }
  100% {
    transform: scale(1); /* Back to original size */
  }
`;

export const Details = styled.div<{ isApp?: boolean; isPulsing?: boolean }>`
  display: block;
  color: ${theme.colors.text.primary};
  font-family: ${theme.typography.fontFamily.primary};
  font-weight: ${theme.typography.fontWeight.regular};
  font-size: ${theme.typography.fontSize.body3};
  line-height: ${theme.typography.lineHeight.body3};

  > div > p {
    transform-origin: left;

    ${({ isPulsing }) =>
      isPulsing &&
      css`
        animation: ${pulse} 2s linear;
      `}
  }

  button {
    min-width: ${pxToRem(97)};

    ${media.tablet`
      min-width: ${pxToRem(209)};
  `}

    ${media.desktop`
      min-width: ${pxToRem(220)};
  `}
  }

  p {
    padding: 0;
    margin: 0;
    font-family: ${({ isApp }) => (isApp ? 'inherit' : theme.typography.fontFamily.primary)};
    font-weight: ${({ isApp }) =>
      isApp ? theme.typography.fontWeight.bold : theme.typography.fontWeight.extrabold};
    font-size: ${theme.typography.fontSize.h5};
    line-height: ${theme.typography.lineHeight.h5};

    ${media.desktop`
        font-size: ${theme.typography.fontSize.h2};
        line-height: ${theme.typography.lineHeight.h2};
    `}

    .rec {
      font-size: ${theme.typography.fontSize.body1};
      line-height: ${theme.typography.lineHeight.body1};

      ${media.tablet`
        font-size: ${theme.typography.fontSize.h5};
        line-height: ${theme.typography.lineHeight.h5};
      `}

      ${media.desktop`
        font-size: ${theme.typography.fontSize.h4};
        line-height: ${theme.typography.lineHeight.h4};
      `}
    }
  }

  ${media.desktop`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;

    .button-slide-container {
      margin-right: ${theme.spacing.md};
    }
  `}
`;

export const DetailsLoading = styled.div`
  width: 160px;
  height: 50px;
  background:
    linear-gradient(0.25turn, transparent, ${theme.colors.bg.primary}, transparent),
    linear-gradient(#eee, #eee), linear-gradient(#eee, #eee);
  background-repeat: no-repeat;
  animation: loading 1.5s infinite;

  ${media.tablet`
    width: 250px;
  `}

  @keyframes loading {
    to {
      background-position:
        315px 0,
        0 0,
        0 190px,
        50px 195px;
    }
  }
`;

export const TopLabel = styled.span`
  p {
    font-size: ${theme.typography.fontSize.caption};
    line-height: ${theme.typography.lineHeight.caption};
  }

  ${media.desktop`
    font-size: ${theme.typography.fontSize.body2};
    line-height: ${theme.typography.lineHeight.body2};
  `}
`;

export const ShowDetailsCta = styled.button<{ rotation: '-90' | '90'; disableUpperCase?: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 0;
  margin: 0;
  background-color: transparent;
  border: none;
  min-width: inherit !important;

  span {
    margin: 0;
    font-family: ${theme.typography.fontFamily.primary};
    font-weight: ${theme.typography.fontWeight.regular};
    font-size: ${theme.typography.fontSize.body2};
    line-height: ${theme.typography.lineHeight.body2};
    color: ${theme.colors.text.primary};

    ${(props) => (props.disableUpperCase ? '' : 'text-transform: uppercase;')}
  }

  svg {
    margin-left: 4px;
    width: 4px;
    height: 8px;
    transform: rotate(${(props) => (props.rotation ? props.rotation : '0')}deg);
  }
`;

// element a style moved from MainContainer to ChildrenContainer
export const ChildrenContainer = styled.div<{
  activationBoxHeight: number;
  ctaContainerWidth: number;
}>`
  -ms-overflow-style: none; /* for Internet Explorer, Edge */
  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */
  }

  overflow-y: auto;
  height: calc(100% - ${(props) => props.activationBoxHeight + 40}px);
  padding: 0 ${theme.spacing.lg};

  ${media.desktop`
    max-width: calc(100% - ${(props) => props.ctaContainerWidth}px);
    margin: 0 auto;
  `}

  a {
    color: ${theme.colors.text.primary};
  }
`;

export const ProgressbarContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  background-color: ${theme.colors.border.default};
  width: 100%;
  padding: 0;
`;

export const Bar = styled.div<{ stepCoverage: number }>`
  min-height: ${progressbarHeight}px;
  width: ${(props) => props.stepCoverage}%;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  background-color: ${theme.colors.brand.primary};
  transition: width 1s ease;
`;

